import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { FileText, CheckCircle, Printer, Lock, AlertTriangle, DollarSign, ShoppingCart, RotateCcw, Users } from "lucide-react";

const formatPrice = (p) => new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", minimumFractionDigits: 0 }).format(p);
const methodLabels = { cash: "Tunai", qris: "QRIS", edc: "EDC Debit/Kredit", kupon: "Kupon" };

export default function DailyReportPage() {
  const { user } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [incidentNotes, setIncidentNotes] = useState("");
  const [closing, setClosing] = useState(false);
  const canClose = ["administrator", "store_manager", "kepala_cabang"].includes(user?.role);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/daily-report/today"); setReport(data); setIncidentNotes(data.incident_notes || ""); } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const closeDay = async () => {
    if (!window.confirm("Tutup laporan hari ini? Antrian akan di-reset dan data hari ini dikunci. Tindakan ini tidak bisa dibatalkan.")) return;
    setClosing(true);
    try {
      await api.post("/daily-report/close", { incident_notes: incidentNotes });
      toast.success("Laporan harian ditutup & antrian di-reset!");
      fetchReport();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal menutup hari"); } finally { setClosing(false); }
  };

  const printReport = () => {
    const r = report;
    if (!r) return;
    const today = new Date().toLocaleDateString("id-ID", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
    const w = window.open("", "_blank", "width=800,height=900");
    if (!w) return;
    const methodRows = (r.by_method || []).map(m => `<tr><td style="padding:4px 8px;border-bottom:1px solid #ddd">${methodLabels[m.method] || m.method}</td><td style="padding:4px 8px;border-bottom:1px solid #ddd;text-align:right;font-family:monospace">${formatPrice(m.total)}</td></tr>`).join("");
    const txRows = (r.transactions_detail || []).map(t => {
      const time = t.created_at ? new Date(t.created_at).toLocaleTimeString("id-ID", {hour:"2-digit",minute:"2-digit"}) : "-";
      const items = (t.items || []).map(it => `${it.name} x${it.qty}`).join(", ");
      return `<tr><td style="padding:3px 6px;border-bottom:1px solid #eee;font-size:9px">${time}</td><td style="padding:3px 6px;border-bottom:1px solid #eee;font-family:monospace;font-size:9px">${t.transaction_code}</td><td style="padding:3px 6px;border-bottom:1px solid #eee">${t.customer_name}${t.customer_phone ? '<br><span style="color:#999;font-size:8px">'+t.customer_phone+'</span>' : ''}</td><td style="padding:3px 6px;border-bottom:1px solid #eee;font-size:9px">${items}</td><td style="padding:3px 6px;border-bottom:1px solid #eee;font-size:9px">${t.cashier_name}</td><td style="padding:3px 6px;border-bottom:1px solid #eee;font-size:9px;text-transform:uppercase">${t.payment_method}</td><td style="padding:3px 6px;border-bottom:1px solid #eee;text-align:right;font-family:monospace">${formatPrice(t.total)}</td></tr>`;
    }).join("");
    w.document.write(`<!DOCTYPE html><html><head><title>Laporan Harian - ${today}</title>
<style>
@page{size:A4;margin:15mm}
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:'Segoe UI',sans-serif;font-size:11px;color:#000;background:#fff;padding:15mm}
h1{font-size:16px;text-align:center;margin-bottom:2px}
.sub{text-align:center;font-size:10px;color:#666;margin-bottom:12px}
.date{text-align:center;font-size:12px;font-weight:bold;margin-bottom:16px;padding:6px;background:#f5f5f5;border:1px solid #ddd}
table{width:100%;border-collapse:collapse;margin-bottom:12px}
th{background:#f0f0f0;padding:6px 8px;text-align:left;font-size:10px;text-transform:uppercase;border-bottom:2px solid #333}
td{padding:4px 8px;border-bottom:1px solid #eee}
.total-row td{font-weight:bold;border-top:2px solid #333;padding-top:8px}
.section{margin-bottom:16px}
.section-title{font-size:11px;font-weight:bold;text-transform:uppercase;letter-spacing:1px;margin-bottom:6px;padding-bottom:3px;border-bottom:1px solid #333}
.incident{border:1px solid #ddd;padding:8px;min-height:50px;margin-bottom:16px;font-style:italic}
.sig-area{margin-top:30px;display:flex;justify-content:space-between;gap:40px}
.sig-box{flex:1;text-align:center;padding-top:60px;border-top:1px solid #333}
.sig-label{font-size:10px;color:#666;margin-top:4px}
.badge{display:inline-block;padding:2px 8px;font-size:9px;font-weight:bold;border-radius:2px}
.closed{background:#d4edda;color:#155724}
.open{background:#fff3cd;color:#856404}
</style></head><body>
<h1>FI BARBERSHOP SEKIP</h1>
<div class="sub">Laporan Harian Operasional</div>
<div class="date">${today} ${r.is_closed ? `<span class="badge closed">DITUTUP oleh ${r.closed_by}</span>` : '<span class="badge open">BELUM DITUTUP</span>'}</div>

<div class="section">
  <div class="section-title">Ringkasan Keuangan</div>
  <table>
    <tr><td>Total POS Kasir</td><td style="text-align:right;font-family:monospace">${formatPrice(r.pos_total)}</td><td style="text-align:right;color:#666">${r.pos_count} transaksi</td></tr>
    <tr><td>Total Toko Online</td><td style="text-align:right;font-family:monospace">${formatPrice(r.online_total)}</td><td style="text-align:right;color:#666">${r.online_count} pesanan</td></tr>
    <tr><td style="color:red">Refund</td><td style="text-align:right;font-family:monospace;color:red">-${formatPrice(r.refund_total)}</td><td style="text-align:right;color:#666">${r.refund_count} refund</td></tr>
    <tr class="total-row"><td><strong>PENDAPATAN BERSIH</strong></td><td style="text-align:right;font-family:monospace;font-size:13px"><strong>${formatPrice(r.net_total)}</strong></td><td></td></tr>
  </table>
</div>

<div class="section">
  <div class="section-title">Rincian Metode Pembayaran</div>
  <table><thead><tr><th>Metode</th><th style="text-align:right">Jumlah</th></tr></thead><tbody>${methodRows}</tbody></table>
</div>

<div class="section">
  <div class="section-title">Antrian</div>
  <table>
    <tr><td>Total Antrian</td><td style="text-align:right">${r.queue_total}</td></tr>
    <tr><td>Dilayani</td><td style="text-align:right">${r.queue_served}</td></tr>
  </table>
</div>

${txRows ? `<div class="section">
  <div class="section-title">Detail Transaksi (${(r.transactions_detail || []).length})</div>
  <table><thead><tr><th>Waktu</th><th>Kode</th><th>Pelanggan</th><th>Item</th><th>Kasir</th><th>Metode</th><th style="text-align:right">Total</th></tr></thead><tbody>${txRows}</tbody>
  <tfoot><tr class="total-row"><td colspan="6"><strong>Total</strong></td><td style="text-align:right;font-family:monospace"><strong>${formatPrice(r.pos_total)}</strong></td></tr></tfoot></table>
</div>` : ""}

<div class="section">
  <div class="section-title">Catatan Insiden</div>
  <div class="incident">${r.incident_notes || incidentNotes || 'Tidak ada insiden'}</div>
</div>

<div class="sig-area">
  <div class="sig-box"><div class="sig-label">Store Manager</div><div style="margin-top:4px;font-size:9px;color:#999">( __________________ )</div></div>
  <div class="sig-box"><div class="sig-label">Finance</div><div style="margin-top:4px;font-size:9px;color:#999">( __________________ )</div></div>
</div>
</body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 300);
  };

  if (loading) return <div className="flex items-center justify-center h-full"><div className="w-8 h-8 border-2 border-[#E60000] border-t-transparent rounded-full animate-spin" /></div>;
  if (!report) return <div className="p-8 text-gray-500 text-center">Gagal memuat laporan</div>;

  return (
    <div className="p-6 md:p-8" data-testid="daily-report-page">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight text-white">Laporan Harian</h1>
          <p className="text-gray-400 text-sm mt-1">{new Date().toLocaleDateString("id-ID", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}</p>
        </div>
        <div className="flex items-center gap-2">
          {report.is_closed && <Badge className="bg-green-900/30 text-green-400 border-green-800/40 text-xs"><Lock size={10} className="mr-1" /> Ditutup oleh {report.closed_by}</Badge>}
          <Button onClick={printReport} className="bg-[#262626] hover:bg-[#333] text-white h-9 rounded-none text-xs px-3" data-testid="print-daily-report"><Printer size={14} className="mr-1" /> Cetak Laporan</Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Card icon={ShoppingCart} label="POS Kasir" value={formatPrice(report.pos_total)} sub={`${report.pos_count} transaksi`} color="text-blue-400" bg="border-blue-800/30 bg-blue-900/10" />
        <Card icon={DollarSign} label="Toko Online" value={formatPrice(report.online_total)} sub={`${report.online_count} pesanan`} color="text-purple-400" bg="border-purple-800/30 bg-purple-900/10" />
        <Card icon={RotateCcw} label="Refund" value={`-${formatPrice(report.refund_total)}`} sub={`${report.refund_count} refund`} color="text-red-400" bg="border-red-800/30 bg-red-900/10" />
        <Card icon={Users} label="Antrian" value={`${report.queue_served}/${report.queue_total}`} sub="dilayani" color="text-green-400" bg="border-green-800/30 bg-green-900/10" />
      </div>

      {/* Net Total */}
      <div className="bg-[#0F0F0F] border border-[#262626] p-5 mb-6 flex items-center justify-between">
        <div>
          <p className="text-gray-500 text-xs uppercase tracking-wider">Pendapatan Bersih Hari Ini</p>
          <p className="text-[#E60000] font-heading text-3xl font-bold">{formatPrice(report.net_total)}</p>
        </div>
        <div className="space-y-1">
          {(report.by_method || []).map((m, i) => (
            <div key={i} className="flex items-center gap-2 text-xs">
              <span className="text-gray-500 w-20">{methodLabels[m.method] || m.method}</span>
              <span className="text-white font-mono">{formatPrice(m.total)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Incident Notes */}
      <div className="bg-[#0F0F0F] border border-[#262626] p-5 mb-6">
        <h2 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3 flex items-center gap-1.5"><AlertTriangle size={14} /> Catatan Insiden Harian</h2>
        {report.is_closed ? (
          <p className="text-gray-400 text-sm">{report.incident_notes || "Tidak ada insiden"}</p>
        ) : (
          <textarea
            className="w-full bg-[#1A1A1A] border border-[#262626] text-white text-sm p-3 rounded-none resize-none h-24 focus:outline-none focus:border-[#E60000]"
            placeholder="Tuliskan catatan insiden jika ada (keluhan pelanggan, kerusakan, dsb)..."
            value={incidentNotes}
            onChange={e => setIncidentNotes(e.target.value)}
            data-testid="incident-notes"
          />
        )}
      </div>

      {/* Detail Transaksi */}
      <div className="bg-[#0F0F0F] border border-[#262626] p-5 mb-6" data-testid="tx-detail-table">
        <h2 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3 flex items-center gap-1.5"><ShoppingCart size={14} /> Detail Transaksi ({(report.transactions_detail || []).length})</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-[11px]"><thead><tr className="border-b border-[#262626]">
            <th className="text-left px-2 py-2 text-gray-500 font-bold">Waktu</th>
            <th className="text-left px-2 py-2 text-gray-500 font-bold">Kode</th>
            <th className="text-left px-2 py-2 text-gray-500 font-bold">Pelanggan</th>
            <th className="text-left px-2 py-2 text-gray-500 font-bold">Layanan/Produk</th>
            <th className="text-left px-2 py-2 text-gray-500 font-bold">Capster</th>
            <th className="text-left px-2 py-2 text-gray-500 font-bold">Kasir</th>
            <th className="text-left px-2 py-2 text-gray-500 font-bold">Metode</th>
            <th className="text-right px-2 py-2 text-gray-500 font-bold">Total</th>
          </tr></thead><tbody>
            {(report.transactions_detail || []).length === 0 && <tr><td colSpan={8} className="text-center py-4 text-gray-600">Belum ada transaksi</td></tr>}
            {(report.transactions_detail || []).map((t, i) => (
              <tr key={i} className="border-b border-[#262626]/30 hover:bg-[#1A1A1A]">
                <td className="px-2 py-1.5 text-gray-400">{new Date(t.created_at).toLocaleTimeString("id-ID", {hour:"2-digit",minute:"2-digit"})}</td>
                <td className="px-2 py-1.5 text-white font-mono text-[10px]">{t.transaction_code}</td>
                <td className="px-2 py-1.5 text-white">{t.customer_name}<br/><span className="text-gray-600 text-[9px]">{t.customer_phone}</span></td>
                <td className="px-2 py-1.5 text-gray-300">{(t.items || []).map(it => `${it.name} x${it.qty}`).join(", ")}</td>
                <td className="px-2 py-1.5 text-gray-400">{t.capster_id ? t.capster_id.slice(0, 8) : "-"}</td>
                <td className="px-2 py-1.5 text-gray-400">{t.cashier_name}</td>
                <td className="px-2 py-1.5"><span className="text-[10px] uppercase text-gray-400">{t.payment_method}</span></td>
                <td className="px-2 py-1.5 text-right font-mono text-green-400">{formatPrice(t.total)}</td>
              </tr>
            ))}
          </tbody></table>
        </div>
      </div>

      {/* Detail Antrian Timeline */}
      <div className="bg-[#0F0F0F] border border-[#262626] p-5 mb-6" data-testid="queue-detail-table">
        <h2 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3 flex items-center gap-1.5"><Users size={14} /> Detail Antrian ({(report.queue_detail || []).length})</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-[11px]"><thead><tr className="border-b border-[#262626]">
            <th className="text-left px-2 py-2 text-gray-500 font-bold">No.</th>
            <th className="text-left px-2 py-2 text-gray-500 font-bold">Pelanggan</th>
            <th className="text-left px-2 py-2 text-gray-500 font-bold">Layanan</th>
            <th className="text-left px-2 py-2 text-gray-500 font-bold">Ambil Antrian</th>
            <th className="text-left px-2 py-2 text-gray-500 font-bold">Dipanggil</th>
            <th className="text-left px-2 py-2 text-gray-500 font-bold">Selesai</th>
            <th className="text-left px-2 py-2 text-gray-500 font-bold">Durasi Tunggu</th>
            <th className="text-left px-2 py-2 text-gray-500 font-bold">Status</th>
          </tr></thead><tbody>
            {(report.queue_detail || []).length === 0 && <tr><td colSpan={8} className="text-center py-4 text-gray-600">Tidak ada data antrian</td></tr>}
            {(report.queue_detail || []).map((q, i) => {
              const created = new Date(q.created_at);
              const called = q.called_at ? new Date(q.called_at) : null;
              const done = q.done_at ? new Date(q.done_at) : null;
              const waitMins = called ? Math.round((called - created) / 60000) : null;
              const fmt = (d) => d ? d.toLocaleTimeString("id-ID", {hour:"2-digit",minute:"2-digit",second:"2-digit"}) : "-";
              const statusColors = { waiting: "text-yellow-400", serving: "text-blue-400", done: "text-green-400", skipped: "text-gray-500" };
              return (
                <tr key={i} className="border-b border-[#262626]/30 hover:bg-[#1A1A1A]">
                  <td className="px-2 py-1.5 text-[#E60000] font-bold font-mono">{String(q.queue_number).padStart(3, "0")}</td>
                  <td className="px-2 py-1.5 text-white">{q.customer_name || "-"}<br/><span className="text-gray-600 text-[9px]">{q.customer_phone || ""}</span></td>
                  <td className="px-2 py-1.5 text-gray-300">{q.service_type}</td>
                  <td className="px-2 py-1.5 text-gray-400">{fmt(created)}</td>
                  <td className="px-2 py-1.5 text-gray-400">{fmt(called)}</td>
                  <td className="px-2 py-1.5 text-gray-400">{fmt(done)}</td>
                  <td className="px-2 py-1.5 text-gray-400">{waitMins !== null ? `${waitMins} menit` : "-"}</td>
                  <td className={`px-2 py-1.5 text-[10px] uppercase font-bold ${statusColors[q.status] || "text-gray-500"}`}>{q.status}</td>
                </tr>
              );
            })}
          </tbody></table>
        </div>
      </div>

      {/* Close Day Button */}
      {!report.is_closed && canClose && (
        <div className="bg-[#0F0F0F] border border-[#E60000]/30 p-5">
          <div className="flex items-center gap-3 mb-3">
            <Lock size={18} className="text-[#E60000]" />
            <div>
              <p className="text-white font-bold text-sm">Tutup Laporan Hari Ini</p>
              <p className="text-gray-500 text-xs">Approve keuangan harian, reset antrian, dan kunci data hari ini. Tidak bisa dibatalkan.</p>
            </div>
          </div>
          <Button onClick={closeDay} disabled={closing} className="w-full bg-[#E60000] hover:bg-[#FF1A1A] text-white font-bold uppercase tracking-wider h-12 rounded-none disabled:opacity-40" data-testid="close-day-btn">
            {closing ? "Memproses..." : <><CheckCircle size={16} className="mr-2" /> Approve & Tutup Hari Ini</>}
          </Button>
        </div>
      )}

      {report.is_closed && (
        <div className="bg-green-900/10 border border-green-800/30 p-5 flex items-center gap-3">
          <CheckCircle size={18} className="text-green-400" />
          <div>
            <p className="text-green-400 font-bold text-sm">Laporan Hari Ini Sudah Ditutup</p>
            <p className="text-gray-500 text-xs">Ditutup oleh {report.closed_by} pada {new Date(report.closed_at).toLocaleString("id-ID")}</p>
          </div>
        </div>
      )}
    </div>
  );
}

function Card({ icon: Icon, label, value, sub, color, bg }) {
  return (
    <div className={`border p-4 ${bg}`}>
      <div className="flex items-center gap-2 mb-2"><Icon size={14} className={color} /><span className="text-gray-400 text-[10px] font-bold uppercase tracking-wider">{label}</span></div>
      <p className={`font-heading text-xl font-bold ${color}`}>{value}</p>
      {sub && <p className="text-gray-500 text-[10px] mt-0.5">{sub}</p>}
    </div>
  );
}
